/**
 * CustomFoodDialog — manual-entry form for per-user custom foods.
 *
 * Per founder:
 *   - Manual entry only, no AI macro lookup
 *   - Soft-delete on the server (log entries that reference a deleted
 *     food still render via their snapshotted macros)
 *   - Editing macros does NOT retroactively update prior log entries
 *     (snapshot at log time in Nutrition.jsx)
 *   - Picker-only entry point — no separate Profile button
 *
 * Used by Nutrition.jsx; pops over the Log-food dialog.
 */
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Save, AlertCircle, Trash2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Same category ids as the static FOODS array — keeps the picker filter
// working without special-case branches.
const CATEGORY_OPTIONS = [
  { id: '', label: '(uncategorized)' },
  { id: 'protein', label: 'Protein' },
  { id: 'carb', label: 'Carb' },
  { id: 'fat', label: 'Fat' },
  { id: 'veg', label: 'Vegetable' },
  { id: 'fruit', label: 'Fruit' },
  { id: 'dairy', label: 'Dairy' },
  { id: 'snack', label: 'Snack' },
  { id: 'beverage', label: 'Beverage' },
  { id: 'mixed', label: 'Mixed dish' },
];

const newForm = () => ({
  name: '',
  brand: '',
  category: '',
  servingLabel: '1 serving',
  servingGrams: '100',
  kcal: '',
  protein: '',
  carbs: '',
  fat: '',
  fiber: '',
  sugar: '',
  sodium: '',
});

const formFromFood = (food) => ({
  name: food.name || '',
  brand: food.brand || '',
  category: food.category || food.cat || '',
  servingLabel: food.defaultServing?.label || '1 serving',
  servingGrams: String(food.defaultServing?.grams ?? '100'),
  kcal: String(food.kcal ?? ''),
  protein: String(food.protein ?? ''),
  carbs: String(food.carbs ?? ''),
  fat: String(food.fat ?? ''),
  fiber: food.fiber != null ? String(food.fiber) : '',
  sugar: food.sugar != null ? String(food.sugar) : '',
  sodium: food.sodium != null ? String(food.sodium) : '',
});

const CustomFoodDialog = ({ editing, onClose, onSaved, onDeleted }) => {
  const isEdit = !!editing;
  const [form, setForm] = useState(() => editing ? formFromFood(editing) : newForm());
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (editing) setForm(formFromFood(editing));
  }, [editing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    if (!form.name.trim()) return 'Name is required.';
    if (!form.kcal || isNaN(parseFloat(form.kcal))) return 'Calories are required.';
    if (parseFloat(form.kcal) < 0) return 'Calories cannot be negative.';
    if (!form.servingLabel.trim()) return 'Serving label is required.';
    const grams = parseFloat(form.servingGrams);
    if (!grams || grams <= 0) return 'Serving grams must be positive.';
    return null;
  };

  const buildPayload = () => ({
    name: form.name.trim(),
    brand: form.brand.trim() || null,
    category: form.category || null,
    kcal: parseFloat(form.kcal) || 0,
    protein: parseFloat(form.protein) || 0,
    carbs: parseFloat(form.carbs) || 0,
    fat: parseFloat(form.fat) || 0,
    fiber: form.fiber ? parseFloat(form.fiber) : null,
    sugar: form.sugar ? parseFloat(form.sugar) : null,
    sodium: form.sodium ? parseFloat(form.sodium) : null,
    defaultServing: {
      label: form.servingLabel.trim(),
      grams: parseFloat(form.servingGrams) || 100,
    },
  });

  const handleSave = async () => {
    const v = validate();
    if (v) { setError(v); return; }
    setError('');
    setSaving(true);
    try {
      const payload = buildPayload();
      const res = isEdit
        ? await axios.patch(`${API}/nutrition/custom-foods/${editing.id}`, payload)
        : await axios.post(`${API}/nutrition/custom-foods`, payload);
      onSaved?.(res.data?.food);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Save failed. Try again.');
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!isEdit) return;
    if (!window.confirm(`Delete "${editing.name}"? Past food log entries that used it will still show their macros.`)) return;
    setDeleting(true);
    setError('');
    try {
      await axios.delete(`${API}/nutrition/custom-foods/${editing.id}`);
      onDeleted?.(editing.id);
    } catch (e) {
      setError('Delete failed. Try again.');
      setDeleting(false);
    }
  };

  // Render via portal-to-body so this dialog stacks ABOVE the Log Food
  // dialog (which is a shadcn/Radix Dialog rendered through its own portal).
  // Without this, our z-[60] loses to Radix's stacking context. Per founder
  // Log Food was hiding this dialog.
  const dialog = (
    <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="custom-food-dialog">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md max-h-[92vh] overflow-y-auto bg-enf-surface-deep border border-enf-surface-border rounded-xl shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-enf-surface-border sticky top-0 bg-enf-surface-deep z-10">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] font-semibold text-enf-text-muted">{isEdit ? 'Edit custom food' : 'New custom food'}</div>
            <div className="font-display text-xl tracking-wide leading-none mt-1 text-enf-text-primary">My food entry</div>
          </div>
          <button type="button" onClick={onClose} className="p-2 hover:bg-enf-surface-elevated rounded-lg" data-testid="custom-food-close">
            <X className="w-4 h-4 text-enf-text-muted" />
          </button>
        </div>

        <div className="p-5 space-y-3">
          <Field label="Name *" testId="custom-food-name">
            <input type="text" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder={`e.g. "Mom's chili"`}
              className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded text-sm text-enf-text-primary placeholder:text-enf-text-muted" data-testid="custom-food-name-input" />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Brand (optional)">
              <input type="text" value={form.brand} onChange={(e) => set('brand', e.target.value)} placeholder="e.g. Trader Joe's"
                className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded text-sm text-enf-text-primary placeholder:text-enf-text-muted" data-testid="custom-food-brand-input" />
            </Field>
            <Field label="Category">
              <select value={form.category} onChange={(e) => set('category', e.target.value)}
                className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded text-sm text-enf-text-primary placeholder:text-enf-text-muted" data-testid="custom-food-category-input">
                {CATEGORY_OPTIONS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </Field>
          </div>

          <div className="grid grid-cols-[2fr_1fr] gap-3">
            <Field label="Serving label *">
              <input type="text" value={form.servingLabel} onChange={(e) => set('servingLabel', e.target.value)} placeholder='e.g. "1 cup", "4 oz", "2 tbsp"'
                className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded text-sm text-enf-text-primary placeholder:text-enf-text-muted" data-testid="custom-food-serving-label" />
            </Field>
            <Field label="Grams per serving *">
              <input type="number" min="1" value={form.servingGrams} onChange={(e) => set('servingGrams', e.target.value)}
                className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded text-sm text-enf-text-primary placeholder:text-enf-text-muted" data-testid="custom-food-serving-grams" />
            </Field>
          </div>

          <div className="border-t border-enf-surface-border pt-3">
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-enf-text-muted mb-2">Per serving</div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Calories *">
                <input type="number" min="0" step="0.1" value={form.kcal} onChange={(e) => set('kcal', e.target.value)}
                  className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded text-sm text-enf-text-primary placeholder:text-enf-text-muted" data-testid="custom-food-kcal" />
              </Field>
              <Field label="Protein (g)">
                <input type="number" min="0" step="0.1" value={form.protein} onChange={(e) => set('protein', e.target.value)}
                  className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded text-sm text-enf-text-primary placeholder:text-enf-text-muted" data-testid="custom-food-protein" />
              </Field>
              <Field label="Carbs (g)">
                <input type="number" min="0" step="0.1" value={form.carbs} onChange={(e) => set('carbs', e.target.value)}
                  className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded text-sm text-enf-text-primary placeholder:text-enf-text-muted" data-testid="custom-food-carbs" />
              </Field>
              <Field label="Fat (g)">
                <input type="number" min="0" step="0.1" value={form.fat} onChange={(e) => set('fat', e.target.value)}
                  className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded text-sm text-enf-text-primary placeholder:text-enf-text-muted" data-testid="custom-food-fat" />
              </Field>
              <Field label="Fiber (g, opt.)">
                <input type="number" min="0" step="0.1" value={form.fiber} onChange={(e) => set('fiber', e.target.value)}
                  className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded text-sm text-enf-text-primary placeholder:text-enf-text-muted" data-testid="custom-food-fiber" />
              </Field>
              <Field label="Sugar (g, opt.)">
                <input type="number" min="0" step="0.1" value={form.sugar} onChange={(e) => set('sugar', e.target.value)}
                  className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded text-sm text-enf-text-primary placeholder:text-enf-text-muted" data-testid="custom-food-sugar" />
              </Field>
              <Field label="Sodium (mg, opt.)" className="col-span-2">
                <input type="number" min="0" step="1" value={form.sodium} onChange={(e) => set('sodium', e.target.value)}
                  className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded text-sm text-enf-text-primary placeholder:text-enf-text-muted" data-testid="custom-food-sodium" />
              </Field>
            </div>
          </div>

          {error && (
            <div className="px-3 py-2 bg-enf-role-danger/10 border border-enf-role-danger/40 rounded text-xs text-enf-accent-fg flex items-center gap-2" data-testid="custom-food-error">
              <AlertCircle className="w-3 h-3" /> {error}
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-enf-surface-border bg-enf-surface-deep sticky bottom-0 flex items-center gap-2 z-10">
          {isEdit && (
            <button type="button" onClick={handleDelete} disabled={deleting || saving}
              className="px-3 py-2 text-sm text-enf-role-warning hover:text-enf-accent-fg flex items-center gap-1 disabled:opacity-50"
              data-testid="custom-food-delete">
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
          )}
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-enf-text-secondary hover:text-enf-text-primary ml-auto" data-testid="custom-food-cancel">
            Cancel
          </button>
          <button type="button" onClick={handleSave} disabled={saving || deleting}
            className="px-5 py-2.5 bg-enf-accent hover:bg-enf-accent-hover text-enf-text-primary rounded-md font-semibold text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
            data-testid="custom-food-save">
            <Save className="w-4 h-4" />
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Save food'}
          </button>
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined'
    ? createPortal(dialog, document.body)
    : dialog;
};

const Field = ({ label, children, className = '' }) => (
  <label className={`block ${className}`}>
    <span className="block text-[10px] uppercase tracking-[0.2em] font-semibold text-enf-text-muted mb-1">{label}</span>
    {children}
  </label>
);

export default CustomFoodDialog;
